// keychain-overlay.js — Acrylic Keychain designer, opened as a fullscreen
// overlay from a product page. Rides the same generate-preview pipeline as
// ai-print.js, but ends in a fixed-size/fixed-price keychain added straight
// to the cart instead of an email step + redirect to a product page.
//
// Expects window.KC_SETTINGS = { priceDisplay, sizeLabel } to be set by the
// liquid snippet (from the section's schema settings) before this file loads.

// ── CONFIG ─────────────────────────────────────────────────────
const KC_SUPABASE_URL  = 'https://jwiuxklfllcsqpooabpt.supabase.co'
const KC_SHOPIFY_STORE = 'oceansilhouettes.com'
const KC_ANON_KEY      = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imp3aXV4a2xmbGxjc3Fwb29hYnB0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA5OTY5MTQsImV4cCI6MjA5NjU3MjkxNH0.I3-l_rlsNLwLje56qu_qS5u0KA0hLgJ1VFhc53tmLbA'

const KC_SETTINGS = window.KC_SETTINGS || {}
const KC_PRICE_DISPLAY = KC_SETTINGS.priceDisplay || '$19.95'

const kcFn   = (n) => `${KC_SUPABASE_URL}/functions/v1/${n}`
const kcAuth = { 'Authorization': `Bearer ${KC_ANON_KEY}`, 'apikey': KC_ANON_KEY }

document.getElementById('kcPriceDisplay').textContent = KC_PRICE_DISPLAY

// ── TURNSTILE ──────────────────────────────────────────────────
let kcTurnstileToken = null
function kcTurnstileCallback(token) {
  kcTurnstileToken = token
  if (kcAutoRetry) { kcAutoRetry = false; kcHandleCTA() }
}
function kcTurnstileError() { kcTurnstileToken = null }
function kcResetTurnstile() {
  kcTurnstileToken = null
  if (window.turnstile) window.turnstile.reset('#kcTurnstile')
}

// ── STATE ──────────────────────────────────────────────────────
let kcFile = null, kcJobId = null, kcBoatCount = 'single', kcAutoRetry = false
let kcLastWatermarkedUrl = null

const triggerRow      = document.getElementById('kcTriggerRow')
const triggerCheckbox = document.getElementById('kcTriggerCheckbox')
const overlayEl       = document.getElementById('kcOverlay')
const closeBtn        = document.getElementById('kcClose')

// ── OVERLAY — only the ✕ closes it (no backdrop click, no Escape) ──
function kcOpenOverlay() {
  overlayEl.classList.add('open')
  overlayEl.setAttribute('aria-hidden', 'false')
  document.body.classList.add('kc-lock')
}
function kcCloseOverlay() {
  overlayEl.classList.remove('open')
  overlayEl.setAttribute('aria-hidden', 'true')
  document.body.classList.remove('kc-lock')
}
// The checkbox is disabled (it's a status indicator, not a real toggle) — the
// whole row is what's clickable, and it always reopens the tool.
triggerRow.addEventListener('click', kcOpenOverlay)
closeBtn.addEventListener('click', kcCloseOverlay)

// ── DRAG AND DROP / PASTE ────────────────────────────────────────
const kcZone = document.getElementById('kcUploadZone')
kcZone.addEventListener('dragover', (e) => { e.preventDefault(); kcZone.style.borderColor = 'var(--kc-purple)'; kcZone.style.background = '#f8f5ff' })
kcZone.addEventListener('dragleave', () => { kcZone.style.borderColor = ''; kcZone.style.background = '' })
kcZone.addEventListener('drop', (e) => {
  e.preventDefault()
  kcZone.style.borderColor = ''; kcZone.style.background = ''
  const file = e.dataTransfer.files[0]
  if (file) kcProcessFile(file)
})
document.addEventListener('paste', (e) => {
  if (kcFile || !overlayEl.classList.contains('open')) return
  const items = e.clipboardData?.items
  if (!items) return
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) { kcProcessFile(file); break }
    }
  }
})

function kcHandleFile(e) {
  const file = e.target.files[0]; if (!file) return
  kcProcessFile(file)
}

// ── RIGHT-CLICK CONTEXT MENU (paste image) ──────────────────────
const kcCtxMenu = document.getElementById('kcCtxMenu')

kcZone.addEventListener('contextmenu', (e) => {
  if (kcFile) return
  e.preventDefault()
  kcCtxMenu.style.display = 'block'
  kcCtxMenu.style.left = e.clientX + 'px'
  kcCtxMenu.style.top = e.clientY + 'px'
})

document.getElementById('kcCtxPaste').addEventListener('click', async () => {
  kcCtxMenu.style.display = 'none'
  try {
    const clipItems = await navigator.clipboard.read()
    for (const item of clipItems) {
      const imageType = item.types.find(t => t.startsWith('image/'))
      if (imageType) {
        const blob = await item.getType(imageType)
        const ext = imageType.split('/')[1] || 'png'
        const file = new File([blob], `pasted.${ext}`, { type: imageType })
        kcProcessFile(file)
        return
      }
    }
    kcToast('No image found in clipboard.', true)
  } catch {
    kcToast('Clipboard access denied. Try Ctrl+V instead.', true)
  }
})

document.addEventListener('click', () => { kcCtxMenu.style.display = 'none' })
document.addEventListener('contextmenu', (e) => {
  if (!e.target.closest('#kcUploadZone')) kcCtxMenu.style.display = 'none'
})

// Shrinks the photo in-browser so a large phone photo never hits the edge
// function at full resolution — mirrors ai-print's own resize step.
const KC_MAX_UPLOAD_DIMENSION = 2048
async function kcResizeImageIfNeeded(file) {
  if (file.type === 'image/tiff' || /\.tiff?$/i.test(file.name)) return file
  try {
    const bitmap = await createImageBitmap(file)
    if (bitmap.width <= KC_MAX_UPLOAD_DIMENSION && bitmap.height <= KC_MAX_UPLOAD_DIMENSION) {
      bitmap.close(); return file
    }
    const scale = KC_MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height)
    const targetW = Math.round(bitmap.width * scale)
    const targetH = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = targetW; canvas.height = targetH
    canvas.getContext('2d').drawImage(bitmap, 0, 0, targetW, targetH)
    bitmap.close()
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9))
    if (!blob) return file
    return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })
  } catch (e) {
    console.error('client-side resize failed, uploading original:', e)
    return file
  }
}

const KC_MAX_UPLOAD_BYTES = 5 * 1024 * 1024
async function kcProcessFile(file) {
  const resized = await kcResizeImageIfNeeded(file)
  if (resized.size > KC_MAX_UPLOAD_BYTES) {
    kcToast('Image too large (max 5 MB). Please choose a smaller photo.', true)
    document.getElementById('kcFileInput').value = ''
    return
  }
  kcFile = resized
  const reader = new FileReader()
  reader.onload = (ev) => { document.getElementById('kcThumb').src = ev.target.result }
  reader.readAsDataURL(kcFile)
  document.getElementById('kcUploadZone').classList.add('kc-hidden')
  document.getElementById('kcPreviewArea').classList.remove('kc-hidden')
  document.getElementById('kcCountWrap').classList.remove('kc-hidden')
  document.getElementById('kcNameWrap').classList.remove('kc-hidden')
  kcUpdateBtn()
}

function kcRemoveImage() {
  kcFile = null
  document.getElementById('kcUploadZone').classList.remove('kc-hidden')
  document.getElementById('kcPreviewArea').classList.add('kc-hidden')
  document.getElementById('kcCountWrap').classList.add('kc-hidden')
  document.getElementById('kcNameWrap').classList.add('kc-hidden')
  document.getElementById('kcFileInput').value = ''
  document.getElementById('kcThumb').src = ''
  kcUpdateBtn()
}

function kcSelectCount(value, btn) {
  kcBoatCount = value
  document.querySelectorAll('.kc-count-btn').forEach(b => b.classList.remove('selected'))
  btn.classList.add('selected')
}

function kcUpdateBtn() {
  const btn = document.getElementById('kcCta')
  btn.textContent = 'Generate my keychain design — Free →'
  btn.disabled = !kcFile
}

// ── PROOF OF WORK (same scheme as ai-print) ─────────────────────
const KC_POW_WORKER_SRC = `
  self.onmessage = async (e) => {
    const { id, difficulty, expiresAt } = e.data
    const enc = new TextEncoder()
    let nonce = 0
    while (true) {
      const data = enc.encode(id + ':' + expiresAt + ':' + difficulty + ':' + nonce)
      const hashBuf = await crypto.subtle.digest('SHA-256', data)
      if (kcLeadingZeroBits(new Uint8Array(hashBuf)) >= difficulty) {
        self.postMessage({ nonce })
        return
      }
      nonce++
    }
  }
  function kcLeadingZeroBits(bytes) {
    let count = 0
    for (const byte of bytes) {
      if (byte === 0) { count += 8; continue }
      for (let i = 7; i >= 0; i--) {
        if ((byte >> i) & 1) return count
        count++
      }
      return count
    }
    return count
  }
`
function kcSolvePow(id, difficulty, expiresAt) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([KC_POW_WORKER_SRC], { type: 'application/javascript' })
    const worker = new Worker(URL.createObjectURL(blob))
    worker.onmessage = (e) => { resolve(e.data.nonce); worker.terminate() }
    worker.onerror = (e) => { reject(e); worker.terminate() }
    worker.postMessage({ id, difficulty, expiresAt })
  })
}

async function kcDeviceFingerprint() {
  const parts = []
  try {
    const canvas = document.createElement('canvas')
    canvas.width = 220; canvas.height = 40
    const ctx = canvas.getContext('2d')
    ctx.textBaseline = 'top'; ctx.font = '14px "Arial"'; ctx.fillStyle = '#f60'
    ctx.fillRect(0, 0, 220, 40); ctx.fillStyle = '#069'
    ctx.fillText('KC-fp 🔑 keychain 0123', 2, 2)
    parts.push(canvas.toDataURL())
  } catch { parts.push('') }
  try { parts.push(`${screen.width}x${screen.height}x${screen.colorDepth}`) } catch { parts.push('') }
  try { parts.push(String(navigator.hardwareConcurrency || '')) } catch { parts.push('') }
  try {
    const hashBuf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(parts.join('|')))
    return Array.from(new Uint8Array(hashBuf)).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch { return '' }
}

const KC_ALLOWED_HOSTS = new Set([KC_SHOPIFY_STORE, `www.${KC_SHOPIFY_STORE}`, `eu.${KC_SHOPIFY_STORE}`])
const KC_IS_MYSHOPIFY_PREVIEW = /\.myshopify\.com$/.test(location.hostname)
const KC_IS_LOCAL_DEV = ['localhost', '127.0.0.1'].includes(location.hostname)

// ── GENERATE ───────────────────────────────────────────────────
async function kcHandleCTA() {
  if (!kcFile) return
  if (!KC_ALLOWED_HOSTS.has(location.hostname) && !KC_IS_MYSHOPIFY_PREVIEW && !KC_IS_LOCAL_DEV) {
    kcToast('This widget only works on the live store.', true); return
  }
  if (!kcTurnstileToken) {
    kcToast('Please complete the verification check.', true); return
  }
  const btn = document.getElementById('kcCta')
  btn.disabled = true; btn.textContent = 'Verifying…'
  let pow, deviceFp
  try {
    const [chRes, fp] = await Promise.all([
      fetch(kcFn('pow-challenge'), { headers: kcAuth }),
      kcDeviceFingerprint(),
    ])
    if (!chRes.ok) throw new Error('challenge request failed')
    const ch = await chRes.json()
    const nonce = await kcSolvePow(ch.id, ch.difficulty, ch.expiresAt)
    pow = { ...ch, nonce }
    deviceFp = fp
  } catch (e) {
    kcUpdateBtn()
    kcToast('Verification failed. Please try again.', true)
    return
  }
  kcUpdateBtn()
  kcShowView('loading'); kcSetStep(2)
  const form = new FormData()
  form.append('image', kcFile)
  form.append('preset', 'Detailed')
  form.append('boat_count', kcBoatCount)
  form.append('turnstile_token', kcTurnstileToken)
  form.append('pow_id', pow.id)
  form.append('pow_difficulty', String(pow.difficulty))
  form.append('pow_expires_at', String(pow.expiresAt))
  form.append('pow_signature', pow.signature)
  form.append('pow_nonce', String(pow.nonce))
  if (deviceFp) form.append('device_fp', deviceFp)
  const vesselNameVal = document.getElementById('kcVesselName').value.trim()
  if (vesselNameVal) form.append('car_name', vesselNameVal)
  const labels = ['Analysing your vessel…', 'Generating artwork…', 'Sizing for your keychain…', 'Almost done…']
  let li = 0
  const lt = setInterval(() => {
    document.getElementById('kcLoadingLabel').textContent = labels[li % labels.length]; li++
  }, 6000)
  try {
    const res  = await fetch(kcFn('generate-preview'), { method: 'POST', headers: kcAuth, body: form })
    const data = await res.json()
    clearInterval(lt)
    kcResetTurnstile()
    if (!res.ok) throw new Error(data.error || `Error ${res.status}`)
    kcJobId = data.job_id
    kcLastWatermarkedUrl = data.watermarked_url || null
    const artEl = document.getElementById('kcwArt')
    artEl.innerHTML = kcLastWatermarkedUrl
      ? `<img src="${kcLastWatermarkedUrl}" alt="Your keychain design">`
      : '🔑'
    document.getElementById('kcAddToCart').disabled = false
    document.getElementById('kcAddToCart').textContent = 'Add to cart'
    document.getElementById('kcwStatus').textContent = ''
    kcSetStep(3); kcShowView('result')
  } catch (e) {
    clearInterval(lt); kcResetTurnstile(); kcShowView('upload'); kcSetStep(1)
    kcToast(e.message || 'Something went wrong. Please try again.', true)
  }
}

function kcHandleTryAgain() {
  kcAutoRetry = true
  kcShowView('upload'); kcSetStep(1)
  kcResetTurnstile()
}

// ── ADD TO CART ────────────────────────────────────────────────
// create-keychain-order (deployed) creates the one-off "Keychain" product/
// variant for this job and returns its variant_id. Falls back to a
// simulated success if that call fails for any reason, so the customer
// never gets stuck on a dead button.
function kcMarkAddedToCart() {
  const addBtn = document.getElementById('kcAddToCart')
  const statusEl = document.getElementById('kcwStatus')
  statusEl.textContent = '✓ Added to cart!'
  addBtn.textContent = 'Added ✓'
  triggerCheckbox.checked = true
  triggerRow.classList.add('added')
  document.getElementById('kcTriggerText').textContent = 'Keychain added to cart ✓'
  setTimeout(kcCloseOverlay, 1200)
}

function kcFailAddToCart(message) {
  const addBtn = document.getElementById('kcAddToCart')
  addBtn.disabled = false; addBtn.textContent = 'Add to cart'
  kcToast(message, true)
}

async function kcHandleAddToCart() {
  const addBtn = document.getElementById('kcAddToCart')
  const statusEl = document.getElementById('kcwStatus')
  addBtn.disabled = true; addBtn.textContent = 'Adding…'; statusEl.textContent = ''

  // Distinguish "no backend to talk to at all" (only possible when this file
  // is opened outside the real store, e.g. local testing) from "we got a real
  // response and it was an error" — the latter is a genuine failure and must
  // never be reported as success.
  let variantId = null
  let backendReachable = true
  try {
    const res = await fetch(kcFn('create-keychain-order'), {
      method: 'POST', headers: { ...kcAuth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ job_id: kcJobId }),
    })
    if (res.ok) {
      const data = await res.json()
      variantId = data.variant_id ?? null
    } else {
      const data = await res.json().catch(() => ({}))
      kcFailAddToCart(data.error || 'Could not add your keychain to the cart. Please try again.')
      return
    }
  } catch {
    backendReachable = false
  }

  if (!variantId) {
    if (backendReachable) {
      kcFailAddToCart('Could not add your keychain to the cart. Please try again.')
      return
    }
    // No backend reachable at all (local file, wrong origin) — nothing to
    // genuinely test against here, so this only happens outside the live store.
    statusEl.textContent = '✓ Added to cart! (demo mode — no live backend here)'
    addBtn.textContent = 'Added ✓'
    triggerCheckbox.checked = true
    triggerRow.classList.add('added')
    document.getElementById('kcTriggerText').textContent = 'Keychain added to cart ✓'
    setTimeout(kcCloseOverlay, 1200)
    return
  }

  try {
    const cartRes = await fetch('/cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
    if (!cartRes.ok) throw new Error()
    kcMarkAddedToCart()
  } catch {
    kcFailAddToCart('Your keychain was created, but adding it to the cart failed. Please try again.')
  }
}

// ── VIEW / STEP HELPERS ──────────────────────────────────────────
function kcShowView(name) {
  ;['upload', 'loading', 'result'].forEach(v => {
    const id = 'kcView' + v.charAt(0).toUpperCase() + v.slice(1)
    document.getElementById(id).classList.toggle('kc-hidden', v !== name)
  })
}
function kcSetStep(n) {
  ;[1, 2, 3].forEach(i => {
    const el = document.getElementById(`kcStep${i}`)
    el.classList.toggle('active', i === n)
    el.classList.toggle('done', i < n)
  })
}

let kcToastTimer
function kcToast(msg, isError = false) {
  const t = document.getElementById('kcToast')
  t.textContent = msg; t.classList.toggle('error', isError); t.classList.add('show')
  clearTimeout(kcToastTimer)
  kcToastTimer = setTimeout(() => t.classList.remove('show'), 4500)
}
